






import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { Action } from 'vuex-class';
import { ElementActionsKeys, ElementActionsPayload } from '../../store/types/element';
import DrawingElement from './Elements/DrawingElement.vue';
import findPathIntersections from 'path-intersection';
import { RootGettersReturns } from '@/store/types';

@Component({
  components: {
    DrawingElement,
  },
})
export default class Eraser extends Vue {
  @Prop(Boolean) erasing!: boolean;
  @Prop(Array) intialPosition!: number[];
  @Prop(Number) getNavOffset!: number;
  @Prop(Function) calculateMouseWithOffset!: (x: number, y: number) => { x: number; y: number };
  @Action(ElementActionsKeys.UPDATE_ELEMENT) updateElement!: (payload: ElementActionsPayload['UPDATE_ELEMENT']) => void;

  @Watch('erasing')
  setDrawing(newVal: boolean) {
    if (!newVal) {
      this.mouseUp();
    }
  }

  mousePosition = [0, 0];
  freeHandPath: string[] = [];
  bufferZone: string[] = [];
  bufferLength = 4;

  wrapperDisplay = 'block';

  get styleWrapper() {
    return {
      transform: `translate3d(${this.intialPosition[0]}px, ${this.intialPosition[1]}px, 0)`,
      display: this.wrapperDisplay,
    };
  }

  get path() {
    return [`M0 0`, ...this.freeHandPath].join(' ');
  }

  mounted() {
    window.addEventListener('mousemove', this.setMousePosition);
    window.addEventListener('touchmove', this.setMousePosition);
  }

  beforeDestroy() {
    window.removeEventListener('mousemove', this.setMousePosition);
    window.removeEventListener('touchmove', this.setMousePosition);
  }

  mouseUp() {
    this.freeHandPath.push(...this.bufferZone);
    this.erase();
    this.freeHandPath = [];
    this.bufferZone = [];
  }

  moveSvg(path: string, x: number, y: number, rotation?: number, width?: number, height?: number) {
    let centerPoint = [0, 0];
    if (rotation && width && height) {
      centerPoint = [width / 2, height / 2];
    }
    const calcAngleDegrees = (x: number, y: number) => {
      return (Math.atan2(y, x) * 180) / Math.PI;
    };

    return path
      .split('M')
      .map(p => {
        if (!p) return '';
        return p
          .split(' L')
          .map(v => {
            const [cordX, cordY] = v.split(' ');
            let newX = x + Number(cordX);
            let newY = y + Number(cordY);
            if (rotation) {
              const diffX = Number(cordX) - centerPoint[0];
              const diffY = Number(cordY) - centerPoint[1];
              const currentAngle = calcAngleDegrees(diffX, diffY);
              const hypotenuse = Math.sqrt(diffX ** 2 + diffY ** 2);
              const newRotation = (currentAngle + rotation) * (Math.PI / 180);
              newX = x + centerPoint[0] + Math.cos(newRotation) * hypotenuse;
              newY = y + centerPoint[1] + Math.sin(newRotation) * hypotenuse;
            }
            return [newX, newY].join(' ');
          })
          .join(' L');
      })
      .join('M');
  }

  erase() {
    const currentPath = this.moveSvg(this.path, this.intialPosition[0], this.intialPosition[1]);
    const elements = (this.$store.getters as RootGettersReturns).boardElements;
    const svgs = elements.filter(el => el.type === 'Drawing');
    svgs.forEach(svg => {
      const data = svg.data as { path: string; viewBox: string };
      const path = data.path;
      const [x, y] = data.viewBox.split(' ');
      const svgPath = this.moveSvg(path, svg.x - Number(x), svg.y - Number(y), svg.rotation, svg.width, svg.height);
      const numberOfIntersections = findPathIntersections(currentPath, svgPath, true);
      if (numberOfIntersections) {
        this.updateElement({
          ...svg,
          deleted: true,
        });
      }
    });
  }

  isTouchEvent(e: TouchEvent | MouseEvent): e is TouchEvent {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return !!(e as any).touches;
  }

  async setMousePosition(e: TouchEvent | MouseEvent) {
    let pos: Touch | MouseEvent;
    if (this.isTouchEvent(e)) {
      pos = e.touches[0];
    } else {
      pos = e;
    }
    this.mousePosition = [pos.clientX, pos.clientY];

    if (this.erasing) {
      const mouseX = this.mousePosition[0] - this.getNavOffset;
      const mouseY = this.mousePosition[1];

      const { x, y } = this.calculateMouseWithOffset(mouseX, mouseY);
      this.bufferZone.push(
        `L${Number(x - this.intialPosition[0]).toFixed(4)} ${Number(y - this.intialPosition[1]).toFixed(4)}`
      );
      if (this.bufferZone.length >= this.bufferLength) {
        this.freeHandPath.push(...this.bufferZone);
        const wrapper = this.$refs.drawingWrapper as HTMLElement;

        this.wrapperDisplay = 'none';
        wrapper.style.display = this.wrapperDisplay;
        wrapper.offsetHeight; // no need to store this anywhere, the reference is enough
        await this.$nextTick;
        this.wrapperDisplay = 'block';
        wrapper.style.display = this.wrapperDisplay;
        this.bufferZone = [];
      }
    }
  }
}
